import React, { useState, useEffect } from 'react';

import {
    Box,
    Container,
    Typography,
    Card,
    CardContent,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import PageLayout from '../components/layout/PageLayout';
import { getActivityLogById } from '../store/slices/dashboardSlice';

const formatLabel = (key) =>
  String(key)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());

const formatModelType = (type) => {
  if (!type) return 'N/A';
  return type
    .replace(/^App\\Models\\/i, '')
    .replace(/\\/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .trim();
};

const ViewLog = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [logData, setLogData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchLogData = async () => {
            try {
                setLoading(true);
                const response = await dispatch(getActivityLogById(id)).unwrap();
                setLogData(response?.data ?? response);
            } catch (error) {
                toast.error(error.message || 'Failed to load log data. Please try again.');
            } finally {
                setLoading(false);
            }
        };

        fetchLogData();
    }, [id]);

    if (loading) {
        return (
            <PageLayout>
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                    <Typography>Loading log details...</Typography>
                </Box>
            </PageLayout>
        );
    }

    if (!logData) {
        return (
            <PageLayout>
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                    <Typography>No log data found.</Typography>
                </Box>
            </PageLayout>
        );
    }


    const subject = logData?.properties?.subject ?? logData?.subject;
    const attributes = logData?.properties?.attributes ?? {};
    const oldValues = logData?.properties?.old_values ?? logData?.properties?.old ?? {};
    const attrKeys = Object.keys(attributes);
    const hasChanges = attrKeys.length > 0;

    return (
        <PageLayout>
            <Container maxWidth="xl" sx={{ p: 3 }}>
                <Box sx={{ mb: 4 }}>
                    <Typography variant="h4" sx={{ fontWeight: 600, color: 'text.primary', mb: 1 }}>
                        Activity Log Details
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        Log ID: {logData?.id} · {logData?.time_ago || ''}
                    </Typography>
                </Box>

                {/* Main log info */}
                <Card sx={{ mb: 3 }}>
                    <CardContent>
                        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                            Log Details
                        </Typography>
                        <TableContainer component={Paper} variant="outlined">
                            <Table size="small">
                                <TableBody>
                                    <TableRow>
                                        <TableCell component="th" scope="row" sx={{ fontWeight: 600 }}>Event</TableCell>
                                        <TableCell>
                                            {(() => {
                                                const event = logData?.event;

                                                if (!event) return 'N/A';

                                                const formattedEvent =
                                                    event.charAt(0).toUpperCase() + event.slice(1);

                                                let color = 'default';

                                                if (event === 'created') color = 'success';
                                                if (event === 'updated') color = 'warning';

                                                return (
                                                    <Chip
                                                        label={formattedEvent}
                                                        color={color}
                                                        size="small"
                                                        variant="filled"
                                                    />
                                                );
                                            })()}
                                        </TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell component="th" scope="row" sx={{ fontWeight: 600 }}>Log Name</TableCell>
                                        <TableCell><Chip label={logData?.log_name || 'N/A'} size="small" /></TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell component="th" scope="row" sx={{ fontWeight: 600 }}>Description</TableCell>
                                        <TableCell>{logData?.description || 'N/A'}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell component="th" scope="row" sx={{ fontWeight: 600 }}>Causer</TableCell>
                                        <TableCell>
                                            {logData?.causer?.name || 'N/A'}
                                            {logData?.causer?.id != null && (
                                                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                                    (ID: {logData.causer.id})
                                                </Typography>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell component="th" scope="row" sx={{ fontWeight: 600 }}>Subject</TableCell>
                                        <TableCell>
                                            {subject ? (
                                                <Box>
                                                    <Typography variant="body2">{subject.name ?? '—'}</Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Type: {formatModelType(subject.type)} {subject.id != null && `· ID: ${subject.id}`}
                                                    </Typography>
                                                </Box>
                                            ) : 'N/A'}
                                        </TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell component="th" scope="row" sx={{ fontWeight: 600 }}>Created At</TableCell>
                                        <TableCell>{logData?.created_at || 'N/A'}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell component="th" scope="row" sx={{ fontWeight: 600 }}>Time Ago</TableCell>
                                        <TableCell>{logData?.time_ago || 'N/A'}</TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </CardContent>
                </Card>

                {/* Property changes */}
                {hasChanges && (
                    <Card sx={{ mb: 3 }}>
                        <CardContent>
                            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                                Attributes & Values
                            </Typography>
                            <TableContainer component={Paper} variant="outlined">
                                <Table size="small">
                                    <TableHead>
                                        <TableRow sx={{ bgcolor: 'grey.100' }}>
                                            <TableCell><strong>Attribute</strong></TableCell>
                                            {logData?.event === 'updated' ? (
                                                <>
                                                    <TableCell><strong>Old Value</strong></TableCell>
                                                    <TableCell><strong>New Value</strong></TableCell>
                                                </>
                                            ) : (
                                                <TableCell><strong>Value</strong></TableCell>
                                            )}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {attrKeys.map((key) => {
                                            let label = formatLabel(key);

                                            // Skip unwanted keys
                                            if (label === 'Id' || label === 'Uuid' || label === 'Deleted At') return null;

                                            // Clean up labels ending with "Id"
                                            if (label.endsWith(' Id')) {
                                                label = label.replace(' Id', '');
                                            }

                                            // Helper to display object or primitive
                                            const renderValue = (value) => {
                                                if (value === null || value === undefined || value === '') return null;
                                                
                                                // Handle boolean-style fields (is_something)
                                                if (key.startsWith('is_')) {
                                                    return Number(value) === 1 ? 'Yes' : 'No';
                                                }
                                                
                                                if (typeof value === 'object') {
                                                    // Try to get a readable property
                                                    if (Array.isArray(value)) {
                                                        return value.map(v => v.name ?? v.title ?? JSON.stringify(v)).join(', ');
                                                    }
                                                    return value.name ?? value.title ?? JSON.stringify(value);
                                                }
                                                return String(value);
                                            };

                                            const oldVal = renderValue(oldValues[key]);
                                            const newVal = renderValue(attributes[key]);

                                            // Skip row if there is no value at all
                                            if (!oldVal && !newVal) return null;

                                            return (
                                                <TableRow key={key}>
                                                    {/* Attribute Name */}
                                                    <TableCell sx={{ fontWeight: 500 }}>{label}</TableCell>

                                                    {/* Old Value (only for updated events) */}
                                                    {logData?.event === 'updated' && <TableCell>{oldVal ?? '—'}</TableCell>}

                                                    {/* New Value */}
                                                    <TableCell>{newVal ?? '—'}</TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </CardContent>
                    </Card>
                )}

                <Box sx={{ mt: 3 }}>
                    <Button
                        variant="contained"
                        onClick={() => navigate(-1)}
                        sx={{ bgcolor: 'primary.main' }}
                    >
                        Go Back
                    </Button>
                </Box>
            </Container>
        </PageLayout>
    );
};

export default ViewLog;